import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import { getBpePurchaseJourney } from "../../api/bpeApi";

function dotColor(ev) {
  if (ev.kind === "payment") return "secondary.main";
  if (ev.kind === "funnel") return "primary.main";
  return "grey.500";
}

function formatAt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

const BPePurchaseJourney = ({ conversationId, refreshKey = 0 }) => {
  const { t } = useTranslation("chat");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [journey, setJourney] = useState(null);

  const load = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getBpePurchaseJourney(conversationId);
      setJourney(data);
    } catch (e) {
      setError(e.message || t("passagensBpe.journeyError"));
      setJourney(null);
    } finally {
      setLoading(false);
    }
  }, [conversationId, t]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const saleStatus = journey?.saleStatus;
  const events = journey?.events || [];

  if (!conversationId) return null;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {t("passagensBpe.journeyTitle")}
        </Typography>
        <Button size="small" onClick={load} disabled={loading} sx={{ textTransform: "none" }}>
          {t("passagensBpe.retry")}
        </Button>
      </Box>

      {saleStatus?.inProgress && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Chip
            size="small"
            color="warning"
            label={t("passagensBpe.saleInProgress")}
          />
          {saleStatus.sourceLabel && (
            <Chip size="small" variant="outlined" label={saleStatus.sourceLabel} />
          )}
          {saleStatus.step && (
            <Chip
              size="small"
              variant="outlined"
              label={t(`passagensBpe.funnelStep.${saleStatus.step}`, saleStatus.step)}
            />
          )}
        </Stack>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={22} />
        </Box>
      )}
      {error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {!loading && !error && events.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("passagensBpe.journeyEmpty")}
        </Typography>
      )}
      {!loading && events.length > 0 && (
        <Stack spacing={0} sx={{ pl: 0.5 }}>
          {events.map((ev, i) => {
            const label =
              ev.kind === "funnel"
                ? `${ev.stepLabel || ev.step}${ev.sourceLabel ? ` (${ev.sourceLabel})` : ""}`
                : ev.message;
            return (
              <Stack key={`${ev.at}-${i}`} direction="row" spacing={1.5} alignItems="stretch">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: 14,
                    pt: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      border: 2,
                      borderColor: dotColor(ev),
                      bgcolor: "background.paper",
                      flexShrink: 0,
                    }}
                  />
                  {i < events.length - 1 && (
                    <Box sx={{ width: 2, flex: 1, minHeight: 20, bgcolor: "divider", my: 0.5 }} />
                  )}
                </Box>
                <Box sx={{ pb: i < events.length - 1 ? 1.5 : 0, flex: 1, minWidth: 0 }}>
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatAt(ev.at)}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default BPePurchaseJourney;
